import { useMemo } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  FAIconName,
  NavItem,
  Text,
} from '@equip.health/ui';
import { useHistory } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import AdminLogo from '~/assets/svg/AdminLogo';
import { SIDE_NAV_WIDTH } from '~/lib/constants';
import { VITE_MAUD_APP_VERSION } from '~/lib/constants/env';
import { AdminRoles } from '~/lib/constants/permissions.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { getAuthorizedLinks, isPathMatch } from '~/lib/util/nav.util';
import { UserMode } from '~/lib/contexts/ModeContext';
import { Path } from '~/routes/constants';

import AccountMenu from './AccountMenu';
import {
  NAV_BOTTOM_PADDING,
  NAV_HORIZONTAL_PADDING,
  NAV_SEARCHBAR_PADDING_TOP,
  NAV_TOP_PADDING,
} from './constants';
import GlobalSearchAdmin from './GlobalSearchAdmin';
import AddNewCta from '../AddNewCta';
import util from '~/lib/util/util';

interface AdminLinkDefinition {
  allow: UserMode[];
  restrictRole?: AdminRoles;
  iconName: FAIconName;
  id: string;
  label: string;
  path: Path;
}

const AdminNav = () => {
  const { roles } = useGlobalContext();
  const { techOpsAdminPortal: isTechOpsAdminPortalEnabled } =
    useFlags<LDFlags>();

  const linkDefinitions: AdminLinkDefinition[] = [
    {
      allow: [UserMode.ADMIN],
      iconName: FAIconName.userGearRegular,
      id: 'users',
      label: 'Users',
      path: Path.ADMIN_USERS,
    },
    {
      allow: [UserMode.ADMIN],
      restrictRole: AdminRoles.ADMIN_MANAGE_CONTENT,
      iconName: FAIconName.penRegular,
      id: 'content',
      label: 'Manage Content',
      path: Path.ADMIN_MANAGE_CONTENT,
    },
    ...(isTechOpsAdminPortalEnabled
      ? [
          {
            allow: [UserMode.ADMIN],
            iconName: FAIconName.headsetRegular,
            id: 'tech-ops',
            label: 'Tech Ops',
            path: Path.ADMIN_TECH_OPS,
          },
        ]
      : []),
  ];

  const sortedLinkDefinitions = [...linkDefinitions].sort(
    (a, b) => b.path.split('/').length - a.path.split('/').length,
  );

  const history = useHistory();
  const { pathname } = history.location;

  const selectedNavItem = sortedLinkDefinitions.find(({ path }) =>
    isPathMatch(path, pathname),
  )?.id;

  const authorizedLinks = useMemo<AdminLinkDefinition[]>(
    () =>
      getAuthorizedLinks(
        linkDefinitions,
        UserMode.ADMIN,
        roles,
      ) as AdminLinkDefinition[],
    [roles, isTechOpsAdminPortalEnabled],
  );

  return (
    <ChakraFlex
      as="nav"
      backgroundColor="primary.10"
      direction="column"
      height="-webkit-fill-available"
      padding={`${NAV_TOP_PADDING}px ${NAV_HORIZONTAL_PADDING}px ${NAV_BOTTOM_PADDING}px`}
      width={SIDE_NAV_WIDTH}
      zIndex={100}
    >
      <ChakraFlex
        justifyContent="left"
        onClick={() => history.push(Path.HOME)}
        paddingLeft="6px"
        style={{ cursor: 'pointer' }}
      >
        <AdminLogo />
      </ChakraFlex>
      <ChakraFlex
        paddingLeft="6px"
        paddingTop={`${NAV_SEARCHBAR_PADDING_TOP}px`}
        width={`calc(${SIDE_NAV_WIDTH} - 58px)`}
        zIndex={5}
      >
        <GlobalSearchAdmin />
      </ChakraFlex>
      <ChakraBox marginTop="16px" px="8px">
        <AddNewCta />
      </ChakraBox>
      <ChakraFlex
        direction="column"
        height="-webkit-fill-available"
        justifyContent="space-between"
        paddingTop="32px"
      >
        <ChakraFlex
          direction="column"
          gap="12px"
          px="8px"
          sx={{ '& > *': { cursor: 'pointer' } }}
        >
          {authorizedLinks.map(({ label, id, iconName, path }) => (
            <NavItem
              iconName={iconName}
              isSelected={selectedNavItem === id}
              key={`NavItem__${id}`}
              label={label}
              onClick={() => history.push(path)}
              tooltipText={label}
            />
          ))}
        </ChakraFlex>
        <ChakraBox>
          <AccountMenu />
          <ChakraFlex justifyContent="space-between" marginTop="12px">
            {VITE_MAUD_APP_VERSION && (
              <Text color="secondary.50" variant="caption">
                {util.getAppVersion()}
              </Text>
            )}
          </ChakraFlex>
        </ChakraBox>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default AdminNav;
